import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useIntl } from '@/react-intl';
import BaseApi from '@/uptk/core/api/BaseApi';
import { Button } from '@/design-system/ds-0/components/Button';
import { Modal } from '@/design-system/ds-0/components/Modal';
import type { Simulation } from '../data/contracts';
import type { StudioCommand } from '../commands/commandCatalog';
import { executeStudioCommand } from '../commands/commandTransport';
import { simulationUrl } from '../moduleStudioRoutes';
import SimulationErrorPanel from './SimulationErrorPanel';
import { useModuleStudio } from '../data/ModuleStudioDataProvider';
import { versionInUse } from './versionInUse';

/** Same catalog, preconditions and confirmation as the thread and Copilot. */
export default function RecreateSimulationAction({
  dossierId,
  version,
  rowsPerEntity = 8,
  seed = 1,
}: {
  dossierId: string;
  version: number;
  rowsPerEntity?: number;
  seed?: number;
}) {
  const intl = useIntl(),
    navigate = useNavigate(),
    params = useParams();
  const { repository } = useModuleStudio();
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(null);
  const t = (id: string) => intl.formatMessage({ id });
  const recreate = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await BaseApi.get(
        '/api/v1/module-studio/dossiers/' +
          encodeURIComponent(dossierId) +
          '/commands',
      );
      const command = (response.data as StudioCommand[]).find(
        (item) => item.key === 'simulate',
      );
      if (!command?.available)
        throw new Error(command?.reason ?? 'studio.command.unavailable');
      const versions = await repository.listModuleSpecVersions(dossierId);
      const current =
        versionInUse(versions) ??
        versions
          .filter((item) => item.status === 'proposed')
          .sort((a, b) => b.version - a.version)[0]?.version;
      if (current !== version) throw new Error('studio.command.stale_version');
      const simulation = await executeStudioCommand<Simulation>(
        dossierId,
        'simulate',
        {
          input: { source: { kind: 'version', version }, rowsPerEntity, seed },
        },
      );
      if (!simulation.id) throw new Error('studio.command.failed');
      setOpen(false);
      void navigate(
        simulationUrl(
          params.portalPrefix ?? 'portal',
          params.workspaceId ?? 'default',
          dossierId,
          simulation.id,
        ),
      );
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button disabled={busy} onClick={() => setOpen(true)}>
        {t('moduleStudio.simRefresh.recreate')}
      </Button>
      <Modal
        open={open}
        title={t('moduleStudio.simRefresh.recreate')}
        closeLabel={t('common.action.cancel')}
        onClose={() => setOpen(false)}
        variant="confirm"
        primaryAction={{
          label: t('moduleStudio.simRefresh.recreate'),
          loading: busy,
          onClick: () => void recreate(),
        }}
        secondaryAction={{
          label: t('common.action.cancel'),
          onClick: () => setOpen(false),
        }}
      >
        <p>
          {intl.formatMessage(
            { id: 'moduleStudio.simRefresh.confirm' },
            { version },
          )}
        </p>
        <SimulationErrorPanel error={error} />
      </Modal>
    </>
  );
}
